<template>
  <loading-spinner
    v-if="isLoading"
    text-align="center"
  />

  <div
    v-if="isLoaded"
  >
    <div
      class="row"
    >
      <div
        :class="widthClass"
      >
        <dropdown
          v-if="!readonly"
          label="Groep toevoegen"
          :options="dropdownOptions"
          @update:value="onDropdownValueUpdated"
        />
      </div>
    </div>
    <div 
      class="row"
    >
      <div
        v-for="group in displayedGroups" 
        :key="group.id" 
        class="my-3"
        :class="groupWidth"
      >
        <group-card
          :group="group"
          :readonly="readonly"
          @deleted="onGroupDeleted"
        />
      </div>
    </div>
    <list-cutoff
      v-if="displayedGroupCount < filteredGroups.length"
      :show-all-button-text="`Toon alle groepen (${filteredGroups.length})`"
      @show-all-button-pressed="onShowAllButtonPressed"
    />
  </div>
</template>

<script>
import { getAllGroups } from '@/api/providers/groups.js';

import Dropdown from '@/components/UI/Dropdown.vue';
import GroupCard from '@/components/Shared/GroupList/GroupCard.vue';
import LoadingSpinner from '@/components/UI/LoadingSpinner.vue';
import ListCutoff from '@/components/UI/ListCutoff.vue';

const State = {
  "loading": 0,
  "loaded": 1,
  "error": 2,
};

export default {
  components: {
    Dropdown,
    GroupCard,
    LoadingSpinner,
    ListCutoff,
  },

  props: {
    groupIds: { type: [Array, null], default: null, },
    initialDisplayedGroupCount: { type: Number, default: 6, },
    fullWidth: { type: Boolean, default: false, },
    addGroupCallback: { type: Function, required: true },
    removeGroupCallback: { type: Function, required: true },
    readonly: { type: Boolean, default: false }
  },

  data() {
    return {
      apiGroups: null,
      groupList: null,
      state: State.loading,
      displayedGroupCount: this.initialDisplayedGroupCount,
    }
  },

  computed: {
    isLoading() {
      return this.state == State.loading;
    },
    isLoaded() {
      return this.state == State.loaded;
    },
    isError() {
      return this.state == State.error;
    },
    filteredGroups() {
      return (this.groupList ?? [])
        .filter(g => !g.isDeleted);
    },
    displayedGroups() {
      return this.filteredGroups
        .slice(0, this.displayedGroupCount);
    },
    addedGroupIds() {
      return (this.groupList ?? [])
        .filter(g => g.isNew)
        .map(g => g.id)
    },
    removedGroupIds() {
      return (this.groupList ?? [])
        .filter(g => g.isDeleted)
        .map(g => g.id)
    },
    widthClass() { 
      return this.fullWidth
        ? 'col-12'
        : 'col-4';
    },
    groupWidth() {
      return this.fullWidth 
        ? 'col-sm-12'
        : 'col-sm-4';
    },
    dropdownOptions() {
      return (this.apiGroups ?? []).map(
        g => {
          return {
            value: g.id,
            label: g.title,
          }
        }
      )
    },
  },

  watch: {
    groupIds(v) {
      if (v == null) return;
      if (this.apiGroups == null) return;

      this.initializeGroupDisplay();
    },

    apiGroups(v) {
      if (v == null) return;
      if (this.groupIds == null) return;

      this.initializeGroupDisplay();
    }
  },

  mounted() {
    this.loadApiGroups()
  },

  methods: {
    loadApiGroups() {
      getAllGroups()
      .then(r => this.apiGroups = r)
      .catch(() => {
        this.apiGroups = [];
        this.state = State.error
      });
    },

    initializeGroupDisplay() {
      this.groupList = this.apiGroups.filter(
        g => this.groupIds.includes(g.id)
      );
      
      this.state = State.loaded;
    },

    onDropdownValueUpdated(id) {
      if (this.groupList.find(g => g.id === id) != null) return;

      let option = this.dropdownOptions.find(o => o.value === id);
      if (option == null) return;

      this.groupList.push({
        id: option.value,
        title: option.label,
        isNew: true
      });
    },

    async submit(entityId) {
      await Promise.all([
        this.submitAddedGroups(entityId),
        this.submitRemovedGroups(entityId)
      ]);
    },

    async submitAddedGroups(entityId) {
      const groupIds = (this.groupList ?? [])
          .filter(g => g.isNew && !g.isDeleted)
          .map(g => g.id);

      if (groupIds.length < 1) return;

      await this.addGroupCallback(entityId, groupIds);
    },

    async submitRemovedGroups(entityId) {
      let groupIds = (this.groupList ?? [])
          .filter(g => g.isDeleted && !g.isNew)
          .map(g => g.id);

      if (groupIds.length < 1) return;

      await this.removeGroupCallback(
        entityId,
        groupIds
      );
    },

    onShowAllButtonPressed() {
      this.displayedGroupCount = Number.MAX_SAFE_INTEGER;
    },

    onGroupDeleted(id) {
      let index = this.groupList.findIndex(g => g.id === id);

      if (index < 0) return;

      this.groupList[index].isDeleted = true;
    }
  },
}
</script>